import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { L10nTranslationModule } from 'angular-l10n';
import { PrimeTemplate, SortEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { map } from 'rxjs/operators';
import { getIsLandingPageEnabled } from '@miq/platform';
import { CommandSet, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, sortAscFactory, sortDesFactory } from '@miq/shared-models';
import { ExpandableResource, leafCounter, sortCollectionWith, treeContainerExtractor } from '@miq/utils';
import { TablePaginatorComponent } from '../table-paginator/table-paginator.component';
import { MiqTableHeaderCheckboxComponent } from '../../vendors/table-header-checkbox.component';
import { CommandsComponent } from './commands/commands.component';
import { TreeDataRowComponent } from './tree-data-row/tree-data-row.component';

const ensureIsDistinctSorting = (
  { field: fieldA, order: orderA }: SortEvent,
  { field: fieldB, order: orderB }: SortEvent = { field: '', order: -1 }
) => {
  return fieldA !== fieldB || orderA !== orderB;
};

/**
 * L2-S5: moved verbatim out of `@shared/components/tree-data-table/tree-data-table.component`,
 * which is now a re-export shim. The reports remote's report list renders it, and it was the
 * remote's last table-shaped seam into `src/app/shared`.
 *
 * The only behavioural edit is how the MIQ 2.0 landing-page flag is read: the host version injected
 * the 868-line `SharedService` purely to call `getIsLandingPageEnabled(sharedService)`. Libraries
 * cannot import `@shared`, and that overload only exists to let a caller bypass the cache. This
 * component never needs to — it reads the same cache through the no-argument
 * `getIsLandingPageEnabled()` from @miq/platform's miq2-flag-cache, which is the value
 * `SharedService.isLandingPageEnabled()` writes into.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-tree-data-table',
  templateUrl: './tree-data-table.component.html',
  styleUrl: './tree-data-table.component.scss',
  imports: [
    NgClass,
    CommandsComponent,
    TableModule,
    PrimeTemplate,
    MiqTableHeaderCheckboxComponent,
    TreeDataRowComponent,
    TablePaginatorComponent,
    UpperCasePipe,
    TitleCasePipe,
    L10nTranslationModule,
  ],
})
export class TreeDataTableComponent<T = any> implements OnInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly paginator = viewChild<TablePaginatorComponent>('paginator');
  title = input<string>();
  resources = input([]);
  columns = input<any[]>([]);
  dataKey = input('id');
  commands = input<CommandSet[]>([]);
  isDownloadReportList = input(false);
  rowChange = output<T>();
  selectedRows = output<Array<ExpandableResource<T>>>();
  selectedResources = [];
  elements = [];
  paginatedElements = [];
  // Pagination properties - note: pageSize here refers to total records, using different name for page size
  totalRecords = 0;
  pageSize = DEFAULT_PAGE_SIZE;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  pageIndex = 0;
  selectedRowsPerPage = 50; // keeping for compatibility
  defaultSorting = '';
  private lastSortEvent: SortEvent;
  private sortingFn;
  private readonly dataSource = signal<Array<ExpandableResource<T>>>([]);
  private readonly treeElements = toObservable(this.dataSource);

  get isLandingPageEnabled(): boolean {
    return getIsLandingPageEnabled();
  }

  ngOnInit() {
    this.dataSource.set(this.resources());
    this.treeElements
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(elements => treeContainerExtractor(elements))
      )
      .subscribe((elements: Array<ExpandableResource<T>>) => {
        this.elements = elements;
        this.totalRecords = leafCounter(this.dataSource() || []);
        this.applyPagination();
        this.cdr.markForCheck();
      });
    const deafultSortColum = this.columns().find(({ defaultSort }) => !!defaultSort);
    this.defaultSorting = deafultSortColum ? deafultSortColum.field : '';
    this.lastSortEvent = { field: deafultSortColum.field, order: 1 };
  }
  ngOnDestroy() {
    /** REQUIRED FOR UNTILDESTROY */
  }
  toggle(element) {
    element.toggle();
    this.dataSource.set([...this.dataSource()]);
  }
  sort($event: SortEvent) {
    if (ensureIsDistinctSorting($event, this.lastSortEvent)) {
      this.lastSortEvent = $event;
      const sortingFactory = $event.order === 1 ? sortAscFactory : sortDesFactory;
      this.sortingFn = sortingFactory($event.field);
      this.dataSource.set(sortCollectionWith(this.resources(), this.sortingFn));
    }
  }
  onSelectionChange($event) {
    this.selectedResources = $event;
    this.selectedRows.emit(this.selectedResources);
  }
  // Pagination methods
  handlePagination(pageNumber: number): void {
    this.pageIndex = pageNumber - 1;
    this.applyPagination();
  }
  handlePageSizeChange(event): void {
    this.pageSize = event.pageSize;
    this.selectedRowsPerPage = event.pageSize; // keep for compatibility
    this.pageIndex = 0;
    this.applyPagination();
  }
  private applyPagination(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedElements = this.elements.slice(startIndex, endIndex);
  }
}
